/*
  MCK 8-piece puzzle engine
  - Replace assets/monthly-puzzle.png each month.
  - To use a Shopify image URL instead, change data-image in index.html.
  - No backend, no database, no external libraries.
*/

const PIECE_COUNT = 8;
const DESKTOP_COLUMNS = 4;
const MOBILE_COLUMNS = 2;
const MOBILE_QUERY = "(max-width: 560px)";
const SNAP_TOLERANCE_RATIO = 0.7;

const app = document.querySelector(".mck-puzzle-app");
const board = document.querySelector("#puzzle-board");
const tray = document.querySelector("#piece-tray");
const guideImage = document.querySelector("#guide-image");
const playAgainButton = document.querySelector("#play-again");
const celebration = document.querySelector("#celebration");
const closeCelebrationButton = document.querySelector("#close-celebration");
const celebrationPlayAgainButton = document.querySelector("#celebration-play-again");
const praiseAudio = document.querySelector("#praise-audio");

const fallbackImage = createFallbackImage();
let puzzleImage = app.dataset.image || "assets/monthly-puzzle.png";
let lockedPieces = 0;
let activeDrag = null;
let praiseAudioUnlocked = false;

preloadImage(puzzleImage)
  .catch(() => {
    puzzleImage = fallbackImage;
  })
  .finally(() => {
    guideImage.src = puzzleImage;
    startGame();
  });

playAgainButton.addEventListener("click", startGame);
celebrationPlayAgainButton.addEventListener("click", startGame);
closeCelebrationButton.addEventListener("click", () => {
  celebration.classList.remove("is-visible");
});

app.addEventListener("pointerdown", unlockPraiseAudio, { once: true });

window.matchMedia(MOBILE_QUERY).addEventListener("change", () => {
  setPieceBackgrounds();
});

function startGame() {
  lockedPieces = 0;
  activeDrag = null;
  celebration.classList.remove("is-visible");
  board.innerHTML = "";
  tray.innerHTML = "";

  createSlots();
  createPieces();
  setPieceBackgrounds();
}

function createSlots() {
  for (let index = 0; index < PIECE_COUNT; index += 1) {
    const slot = document.createElement("div");
    slot.className = "puzzle-slot";
    slot.dataset.slot = String(index);
    board.append(slot);
  }
}

function createPieces() {
  shuffledIndexes().forEach((index) => {
    const piece = document.createElement("button");
    piece.className = "piece";
    piece.type = "button";
    piece.dataset.piece = String(index);
    piece.setAttribute("aria-label", `Puzzle piece ${index + 1}`);
    piece.addEventListener("pointerdown", beginDrag);
    tray.append(piece);
  });
}

function beginDrag(event) {
  const piece = event.currentTarget;

  if (piece.classList.contains("is-locked")) {
    return;
  }

  event.preventDefault();

  const rect = piece.getBoundingClientRect();
  const placeholder = document.createElement("div");
  placeholder.className = "piece-placeholder";
  placeholder.style.width = `${rect.width}px`;
  placeholder.style.height = `${rect.height}px`;

  piece.after(placeholder);
  piece.classList.add("is-dragging");
  piece.style.position = "fixed";
  piece.style.left = `${rect.left}px`;
  piece.style.top = `${rect.top}px`;
  piece.style.width = `${rect.width}px`;
  piece.style.height = `${rect.height}px`;
  document.body.append(piece);

  activeDrag = {
    piece,
    placeholder,
    offsetX: event.clientX - rect.left,
    offsetY: event.clientY - rect.top,
  };

  piece.setPointerCapture(event.pointerId);
  piece.addEventListener("pointermove", moveDrag);
  piece.addEventListener("pointerup", endDrag);
  piece.addEventListener("pointercancel", cancelDrag);
}

function moveDrag(event) {
  if (!activeDrag) {
    return;
  }

  activeDrag.piece.style.left = `${event.clientX - activeDrag.offsetX}px`;
  activeDrag.piece.style.top = `${event.clientY - activeDrag.offsetY}px`;
}

function endDrag(event) {
  if (!activeDrag) {
    return;
  }

  const { piece } = activeDrag;
  const slot = findMatchingSlot(piece, event.clientX, event.clientY);

  if (slot) {
    lockPieceIntoSlot(piece, slot);
  } else {
    returnPieceToTray();
  }

  cleanupDrag(event.pointerId);
}

function cancelDrag(event) {
  if (activeDrag) {
    returnPieceToTray();
    cleanupDrag(event.pointerId);
  }
}

function findMatchingSlot(piece, pointerX, pointerY) {
  const correctSlot = board.querySelector(`.puzzle-slot[data-slot="${piece.dataset.piece}"]`);

  if (!correctSlot || correctSlot.classList.contains("is-filled")) {
    return null;
  }

  const slotRect = correctSlot.getBoundingClientRect();
  const pieceRect = piece.getBoundingClientRect();
  const pieceCenterX = pieceRect.left + pieceRect.width / 2;
  const pieceCenterY = pieceRect.top + pieceRect.height / 2;
  const slotCenterX = slotRect.left + slotRect.width / 2;
  const slotCenterY = slotRect.top + slotRect.height / 2;
  const distance = Math.hypot(pieceCenterX - slotCenterX, pieceCenterY - slotCenterY);
  const tolerance = Math.max(slotRect.width, slotRect.height) * SNAP_TOLERANCE_RATIO;
  const pointerIsInsideCorrectSlot =
    pointerX >= slotRect.left &&
    pointerX <= slotRect.right &&
    pointerY >= slotRect.top &&
    pointerY <= slotRect.bottom;

  return distance <= tolerance || pointerIsInsideCorrectSlot ? correctSlot : null;
}

function lockPieceIntoSlot(piece, slot) {
  activeDrag.placeholder.remove();
  resetInlineDragStyles(piece);

  piece.classList.remove("is-dragging");
  piece.classList.add("is-locked");
  piece.setAttribute("aria-label", `Placed puzzle piece ${Number(piece.dataset.piece) + 1}`);

  slot.classList.add("is-filled");
  slot.append(piece);
  lockedPieces += 1;
  celebrateCorrectPiece(slot);
  playSuccessSound();

  if (lockedPieces === PIECE_COUNT) {
    window.setTimeout(showCelebration, 350);
  }
}

function returnPieceToTray() {
  const { piece, placeholder } = activeDrag;
  resetInlineDragStyles(piece);
  piece.classList.remove("is-dragging");
  placeholder.replaceWith(piece);
}

function cleanupDrag(pointerId) {
  const { piece } = activeDrag;
  piece.releasePointerCapture(pointerId);
  piece.removeEventListener("pointermove", moveDrag);
  piece.removeEventListener("pointerup", endDrag);
  piece.removeEventListener("pointercancel", cancelDrag);
  activeDrag = null;
}

function resetInlineDragStyles(piece) {
  piece.style.position = "";
  piece.style.left = "";
  piece.style.top = "";
  piece.style.width = "";
  piece.style.height = "";
}

function setPieceBackgrounds() {
  const isMobile = window.matchMedia(MOBILE_QUERY).matches;
  const columns = isMobile ? MOBILE_COLUMNS : DESKTOP_COLUMNS;
  const rows = PIECE_COUNT / columns;

  document.querySelectorAll(".piece").forEach((piece) => {
    const index = Number(piece.dataset.piece);
    const column = index % columns;
    const row = Math.floor(index / columns);
    const x = columns === 1 ? 0 : (column / (columns - 1)) * 100;
    const y = rows === 1 ? 0 : (row / (rows - 1)) * 100;

    piece.style.backgroundImage = `url("${puzzleImage}")`;
    piece.style.backgroundPosition = `${x}% ${y}%`;
  });
}

function showCelebration() {
  celebration.classList.add("is-visible");
  playPraiseAudio();
  launchConfetti();
}

function playPraiseAudio() {
  if (!praiseAudio) {
    return;
  }

  praiseAudio.currentTime = 0;
  praiseAudio.play().catch(() => {
    // Some browsers block audio if the puzzle was completed without a user gesture.
  });
}

function unlockPraiseAudio() {
  if (!praiseAudio || praiseAudioUnlocked) {
    return;
  }

  praiseAudioUnlocked = true;
  praiseAudio.muted = true;
  praiseAudio
    .play()
    .then(() => {
      praiseAudio.pause();
      praiseAudio.currentTime = 0;
      praiseAudio.muted = false;
    })
    .catch(() => {
      praiseAudio.muted = false;
    });
}

function celebrateCorrectPiece(slot) {
  slot.classList.remove("just-placed");
  void slot.offsetWidth;
  slot.classList.add("just-placed");

  const sparkle = document.createElement("span");
  sparkle.className = "snap-sparkle";
  sparkle.textContent = "★";
  slot.append(sparkle);
  sparkle.addEventListener("animationend", () => sparkle.remove());
}

function playSuccessSound() {
  const AudioContext = window.AudioContext || window.webkitAudioContext;

  if (!AudioContext) {
    return;
  }

  const audio = new AudioContext();
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();

  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(560, audio.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(760, audio.currentTime + 0.12);
  gain.gain.setValueAtTime(0.0001, audio.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.045, audio.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + 0.16);

  oscillator.connect(gain);
  gain.connect(audio.destination);
  oscillator.start();
  oscillator.stop(audio.currentTime + 0.17);
  oscillator.addEventListener("ended", () => audio.close());
}

function launchConfetti() {
  const colors = ["#8009be", "#faec0d", "#4fbe09", "#ff7ab6", "#35c7ff"];

  for (let index = 0; index < 34; index += 1) {
    const confetti = document.createElement("span");
    confetti.className = "confetti";
    confetti.style.left = `${Math.random() * 100}%`;
    confetti.style.background = colors[index % colors.length];
    confetti.style.animationDelay = `${Math.random() * 0.28}s`;
    confetti.style.transform = `rotate(${Math.random() * 180}deg)`;
    document.body.append(confetti);

    confetti.addEventListener("animationend", () => confetti.remove());
  }
}

function shuffledIndexes() {
  const indexes = Array.from({ length: PIECE_COUNT }, (_, index) => index);

  for (let index = indexes.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [indexes[index], indexes[randomIndex]] = [indexes[randomIndex], indexes[index]];
  }

  return indexes;
}

function preloadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = resolve;
    image.onerror = reject;
    image.src = src;
  });
}

function createFallbackImage() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 1200">
      <rect width="1200" height="1200" fill="#dff6ff"/>
      <circle cx="240" cy="190" r="130" fill="#faec0d"/>
      <rect x="0" y="850" width="1200" height="350" fill="#4fbe09"/>
      <path d="M140 880 C320 610 470 630 640 880 Z" fill="#8009be"/>
      <path d="M520 880 C710 540 910 550 1080 880 Z" fill="#b85be2"/>
      <circle cx="600" cy="520" r="120" fill="#fffdf2"/>
      <circle cx="558" cy="498" r="16" fill="#2b1640"/>
      <circle cx="642" cy="498" r="16" fill="#2b1640"/>
      <path d="M545 560 Q600 612 655 560" fill="none" stroke="#2b1640" stroke-width="18" stroke-linecap="round"/>
      <text x="600" y="1030" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="74" font-weight="900" fill="#2b1640">MCK Story Puzzle</text>
    </svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}